import { useQueries } from "react-query";
import i18next from "i18next";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getOneSsqApiKey, SsqSchema } from "src/api/sure-api/ssq/useGetOneSsq";
import type { SsqScore } from "src/api/sure-api/ssq/useGetOneSsq";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

const containsScore = (scores: SsqScore[], targetId: number): boolean => {
    return scores.some((s) => s.id === targetId || (s.children !== undefined && containsScore(s.children, targetId)));
};

export const useSsqIdForScore = (scoreId: number): number | undefined => {
    const { selectedCompany } = useFoodpilotContext();
    const companySector = selectedCompany?.organizationSector;
    const primarySsqId = companySector?.primarySsqId ?? companySector?.ssqId;
    const ssqs = companySector?.ssqs;

    const ssqQueries = useQueries(
        (ssqs ?? []).map((ssq) => ({
            queryKey: [getOneSsqApiKey, ssq.ssqId, i18next.resolvedLanguage],
            queryFn: () => sureApiCall.get(`/api/ssqs/${ssq.ssqId}`),
            cacheTime: 300000,
            staleTime: 180000,
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retryOnMount: false,
            enabled: scoreId > 0,
        })),
    );

    if (!scoreId || scoreId <= 0) return primarySsqId;
    if (!ssqs?.length) return primarySsqId;

    for (let i = 0; i < ssqQueries.length; i++) {
        const response = ssqQueries[i]?.data;
        if (!response) continue;

        const ssqData = zodParseWithLog(response.data, SsqSchema, getOneSsqApiKey);
        if (ssqData && containsScore(ssqData.scores, scoreId)) {
            return ssqs[i].ssqId;
        }
    }

    return primarySsqId;
};
